import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/api';
import { io, Socket } from 'socket.io-client';

export interface RiskSettings {
  riskPercent: number;
  leverage: number;
  stopLoss: number;
  takeProfit: number;
  maxDailyLoss: number;
  minVolume: number;
  maxBalanceUsage: number;
  maxConcurrentTrades: number;
  isAdvisor: boolean;
  telegramChatId: string;
}

export interface Coin {
  symbol: string;
  price: number;
  change: number;
  score: number;
  volume: number;
  signal: string;
  rsi?: number;
}

export interface Trade {
  id: number;
  symbol: string;
  type: 'BUY' | 'SELL';
  price: number;
  amount: number;
  profit: number | null;
  stopLoss: number | null;
  takeProfit: number | null;
  exitPrice: number | null;
  closedAt: string | null;
  leverage: number;
  isPaper: boolean;
  isClosed: boolean;
  timestamp: string;
}

export interface WeeklyStat {
  date: string;
  profit: number;
}

const SOCKET_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3020' 
  : 'https://probot.codes.uz';

export const useTrading = (isAuthenticated: boolean) => {
  const [coins, setCoins] = useState<Coin[]>([]);
  const [history, setHistory] = useState<Trade[]>([]); 
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStat[]>([]);
  const [stats, setStats] = useState({ dailyProfit: '0.00', winRate: '0', activeOrders: 0 });
  const [balance, setBalance] = useState({ total: 0, free: 0 });
  const [btcStatus, setBtcStatus] = useState({ isSafe: true, btcChange: 0 });
  
  const [isBotRunning, setIsBotRunning] = useState(false);
  const [isPaperMode, setIsPaperMode] = useState(true);
  const [userData, setUserData] = useState<any>(null);

  const [logs, setLogs] = useState<string[]>([]);
  const [limit, setLimit] = useState(10);

  const [riskSettings, setRiskSettings] = useState<RiskSettings>({
    riskPercent: 1.0, leverage: 5, stopLoss: 2.0, takeProfit: 6.0, 
    maxDailyLoss: 3.0, minVolume: 1500000, maxBalanceUsage: 100, 
    maxConcurrentTrades: 3, isAdvisor: false, telegramChatId: ''
  });

  const [lastAlertSymbol, setLastAlertSymbol] = useState('');
  const [isAlertActive, setIsAlertActive] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => { audioRef.current = new Audio('/alert.mp3'); }, []);

  const addLog = useCallback((msg: string) => {
    const time = new Date().toLocaleTimeString();
    setLogs(prev => [`[${time}] ${msg}`, ...prev].slice(0, 50));
  }, []);

  const triggerAlert = useCallback((symbol: string) => {
    setIsAlertActive(prev => {
        if (prev) return true;
        if (audioRef.current) audioRef.current.play().catch(() => {});
        setTimeout(() => setIsAlertActive(false), 5000);
        return true;
    });
  }, []);

  // --- HTTP FETCHERS ---

  const fetchFastData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [statusRes, logsRes, btcRes] = await Promise.all([
        api.get('/users/me'),       
        api.get('/trading/logs'),   
        api.get('/scanner/btc-health') 
      ]);

      const user = statusRes.data;
      setUserData(user); 

      if (user.telegramChatId !== riskSettings.telegramChatId || user.isBotActive !== isBotRunning) {
          setIsBotRunning(user.isBotActive);
          setIsPaperMode(user.isPaperTrading);
          setRiskSettings(prev => ({ ...prev, ...user, telegramChatId: user.telegramChatId || '' }));
      }

      if (logsRes.data && logs.length === 0) setLogs(logsRes.data);
      if (btcRes.data) setBtcStatus(btcRes.data);

    } catch (e) { console.error("Fast poll error", e); }
  }, [isAuthenticated]);

  const fetchScannerData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get(`/scanner/candidates?limit=${limit}`);
      if (Array.isArray(res.data)) {
        setCoins(res.data);
        if (res.data.length > 0) {
          const top = res.data[0];
          if (top.score > 85 && top.symbol !== lastAlertSymbol) {
             triggerAlert(top.symbol);
             setLastAlertSymbol(top.symbol);
          }
        }
      }
    } catch (e) { console.error("Scanner poll error", e); }
  }, [isAuthenticated, limit, lastAlertSymbol]);

  const fetchSlowData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [histRes, statsRes, weeklyRes, balRes] = await Promise.all([
        api.get('/trading/history'),
        api.get('/trading/stats'),
        api.get('/trading/weekly-stats'),
        api.get('/exchange/balance')
      ]);
      if (Array.isArray(histRes.data)) setHistory(histRes.data);
      if (statsRes.data) setStats(statsRes.data);
      if (Array.isArray(weeklyRes.data)) setWeeklyStats(weeklyRes.data);
      if (balRes.data) setBalance(balRes.data);
    } catch (e) { console.error("Slow poll error", e); }
  }, [isAuthenticated]);

  // ============================================================
  // 🔥 WEBSOCKET CONNECTION 🔥
  // ============================================================
  useEffect(() => {
    if (!isAuthenticated) return;

    // Закрываем старое соединение, если есть
    if (socketRef.current) {
        socketRef.current.disconnect();
    }

    console.log(`🔌 Connecting to Socket: ${SOCKET_URL} (Path: /api/socket.io)`);

    socketRef.current = io(SOCKET_URL, {
        path: '/api/socket.io', // 👈 ДОЛЖНО СОВПАДАТЬ С БЭКЕНДОМ
        transports: ['websocket', 'polling'], // Пробуем WS, если нет - поллинг
        reconnectionAttempts: 10,
        reconnectionDelay: 3000,
        withCredentials: true,
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
        console.log('✅ Connected to WebSocket Server ID:', socket.id);
        addLog('🔌 Соединение с сервером установлено');
        
        // Сразу входим в комнату пользователя
        if (userData?.id) {
            socket.emit('join', userData.id);
        }
    });

    socket.on('connect_error', (err) => {
        console.error('❌ Socket Connection Error:', err.message);
    });

    socket.on('disconnect', (reason) => {
        console.warn('⚠️ Socket Disconnected:', reason);
        if (reason === 'io server disconnect') {
            socket.connect();
        }
    });

    // 📩 Слушаем обновление рынка (Баланс, PnL)
    socket.on('market_update', (data: any) => {
        if (data.stats) setStats(data.stats);
        if (data.balance) setBalance(data.balance);
        fetchSlowData(); 
    });

    // 📩 Слушаем СИСТЕМНЫЕ ЛОГИ (Терминал)
    socket.on('system_log', (logEntry: any) => {
        // console.log('📥 Log received:', logEntry); // Раскомментируй для отладки
        const time = new Date(logEntry.timestamp || Date.now()).toLocaleTimeString();
        // Добавляем новый лог в начало массива
        setLogs(prev => [`[${time}] ${logEntry.message}`, ...prev].slice(0, 50));
    });

    return () => {
        if (socketRef.current) socketRef.current.disconnect();
    };
  }, [isAuthenticated, userData?.id]);

  // 🔥 ВАЖНОЕ ДОБАВЛЕНИЕ: Вход в комнату пользователя при получении ID
  // Без этого бэкенд не будет знать, кому слать данные!
  useEffect(() => {
      if (socketRef.current && userData?.id) {
          console.log(`🔑 Entering secure room: user_${userData.id}`);
          socketRef.current.emit('join', userData.id);
      }
  }, [userData]); 

  // --- ИНИЦИАЛИЗАЦИЯ ---
  useEffect(() => {
    if (!isAuthenticated) return;

    fetchFastData();
    fetchScannerData();
    fetchSlowData();

    const scannerInterval = setInterval(fetchScannerData, 10000);
    const userInterval = setInterval(fetchFastData, 10000);

    return () => {
      clearInterval(scannerInterval);
      clearInterval(userInterval);
    };
  }, [isAuthenticated, fetchFastData, fetchScannerData, fetchSlowData]);

  const fetchAllData = useCallback(() => {
    fetchFastData();
    fetchScannerData();
    fetchSlowData();
  }, [fetchFastData, fetchScannerData, fetchSlowData]);

  return {
    coins, history, stats, isBotRunning, isPaperMode, balance, btcStatus, 
    weeklyStats, logs, limit, setLimit, riskSettings, setRiskSettings, 
    userData, 
    isAlertActive, setIsAlertActive, addLog, fetchAllData, setIsBotRunning, setIsPaperMode
  };
};